import React, { useState, useEffect } from 'react';
import { connectorService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import ConnectorBrowser from './ConnectorBrowser';
import ConnectorStatus from './ConnectorStatus';
import {
  Plus,
  AlertTriangle,
  RefreshCw,
  Database,
  Lock
} from 'lucide-react';

function ConnectorsTab() {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBrowser, setShowBrowser] = useState(false);
  const { notify } = useNotification();

  useEffect(() => {
    fetchConnectors();
  }, []);

  const fetchConnectors = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await connectorService.getConnectors();
      setConnectors(response.connectors || []);
    } catch (err) {
      console.error('Error fetching connectors:', err);
      setError('Failed to load connectors');
    } finally {
      setLoading(false);
    }
  };

  const handleAddConnector = () => {
    setShowBrowser(true);
  };

  const handleConnectorAdded = (data) => {
    setShowBrowser(false);
    fetchConnectors(); // Refresh the list
    notify.success('New connector added successfully!');
  };

  const handleSyncConnector = async (connectorId) => {
    try {
      await connectorService.syncConnector(connectorId);
      notify.success('Sync triggered successfully');
      // Refresh connector status
      setTimeout(fetchConnectors, 2000);
    } catch (err) {
      console.error('Error syncing connector:', err);
      notify.error('Failed to trigger sync');
    }
  };

  const handleDeleteConnector = async (connectorId) => {
    if (!window.confirm('Are you sure you want to delete this connector? This will stop all data syncing.')) {
      return;
    }

    try {
      await connectorService.deleteConnector(connectorId);
      notify.success('Connector deleted successfully');
      fetchConnectors(); // Refresh the list
    } catch (err) {
      console.error('Error deleting connector:', err);
      notify.error('Failed to delete connector');
    }
  };

  if (showBrowser) {
    return (
      <ConnectorBrowser
        onConnectorAdded={handleConnectorAdded}
        onBack={() => setShowBrowser(false)}
      />
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-primary-600 dark:border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-slate-600 dark:text-slate-400">Loading connectors...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">
          Failed to Load Connectors
        </h3>
        <p className="text-red-600 dark:text-red-500 mb-4">{error}</p>
        <button
          onClick={fetchConnectors}
          className="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white flex items-center">
            <Database className="w-6 h-6 mr-2" />
            Data Connectors
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage your automated data connections and sync status
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchConnectors}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={handleAddConnector}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Connector
          </button>
        </div>
      </div>

      {/* Access Required Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
              Access Key Required
            </h3>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Data connectors require an access key or special permission to use. Please contact support to enable connector access for your account.
            </p>
          </div>
        </div>
      </div>

      {/* Connectors List - Always show empty state */}
      <div className="text-center py-16 px-4">
        <Database className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-6" />
        <h3 className="text-xl font-semibold text-slate-600 dark:text-slate-400 mb-3">
          No Data Connectors Yet
        </h3>
        <p className="text-slate-500 dark:text-slate-500 mb-6 max-w-md mx-auto">
          Connect your databases, SaaS applications, and other data sources to automatically sync data into your account.
        </p>
        <button
          onClick={handleAddConnector}
          className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors inline-flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Your First Connector
        </button>
      </div>
    </div>
  );
}

export default ConnectorsTab;